import { useEffect, useRef, useState, useCallback } from "react";
import { api } from "@/lib/api";

const LAST_SEEN_KEY = "swiftcash:admin:enquiries:lastSeen";
const MUTED_KEY = "swiftcash:admin:enquiries:muted";

// Shared AudioContext (lazily created — browsers require user gesture to unlock).
let _audioCtx = null;
function getAudioCtx() {
  if (typeof window === "undefined") return null;
  if (_audioCtx) return _audioCtx;
  const Ctor = window.AudioContext || window.webkitAudioContext;
  if (!Ctor) return null;
  try {
    _audioCtx = new Ctor();
    return _audioCtx;
  } catch (_e) { return null; }
}

/**
 * Play a short 2-note glass/bell chime via Web Audio API.
 * Silent no-op if the browser blocks or context is unavailable.
 */
export function playChime({ volume = 0.28 } = {}) {
  const ctx = getAudioCtx();
  if (!ctx) return;
  try {
    if (ctx.state === "suspended") ctx.resume();
    const t0 = ctx.currentTime;
    const notes = [
      { freq: 987.77, start: 0.0, dur: 0.55 }, // B5
      { freq: 1318.51, start: 0.11, dur: 0.6 }, // E6
    ];
    notes.forEach(({ freq, start, dur }) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = freq;
      // ADSR-ish envelope for a soft bell
      gain.gain.setValueAtTime(0.0001, t0 + start);
      gain.gain.exponentialRampToValueAtTime(volume, t0 + start + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, t0 + start + dur);
      osc.connect(gain).connect(ctx.destination);
      osc.start(t0 + start);
      osc.stop(t0 + start + dur + 0.02);
    });
  } catch (_e) { /* silent */ }
}

export function isChimeMuted() {
  if (typeof window === "undefined") return false;
  return localStorage.getItem(MUTED_KEY) === "1";
}
export function setChimeMuted(muted) {
  if (typeof window === "undefined") return;
  if (muted) localStorage.setItem(MUTED_KEY, "1");
  else localStorage.removeItem(MUTED_KEY);
}

/**
 * Polls /api/admin/lender-enquiries and returns { unread, muted, setMuted, markAllRead, refresh, testChime }.
 * - unread = count of enquiries with created_at > last-seen timestamp
 * - Triggers a browser Notification + soft chime whenever a NEW enquiry lands.
 * - Persists last-seen timestamp + muted preference in localStorage.
 */
export function useEnquiryNotifier({ enabled = true, pollMs = 25000 } = {}) {
  const [unread, setUnread] = useState(0);
  const [items, setItems] = useState([]);
  const [muted, setMutedState] = useState(isChimeMuted());
  const seenIdsRef = useRef(new Set()); // in-session ids we already notified about
  const primedRef = useRef(false); // first poll just primes the set
  const mutedRef = useRef(muted); // keep latest value accessible to notify()

  useEffect(() => { mutedRef.current = muted; }, [muted]);

  const lastSeenAt = () => localStorage.getItem(LAST_SEEN_KEY) || "";

  const markAllRead = useCallback(() => {
    const now = new Date().toISOString();
    localStorage.setItem(LAST_SEEN_KEY, now);
    setUnread(0);
  }, []);

  const setMuted = useCallback((v) => {
    setChimeMuted(v);
    setMutedState(v);
  }, []);

  const notify = (enq) => {
    // Sound first — most robust across browsers, doesn't require permissions
    if (!mutedRef.current) playChime();
    try {
      if (typeof window === "undefined" || !("Notification" in window)) return;
      if (Notification.permission !== "granted") return;
      const n = new Notification("New panel enquiry — SwiftCash", {
        body: `${enq.company} · ${enq.name}${enq.ticket_size ? ` · ${enq.ticket_size}` : ""}`,
        tag: `swiftcash-enquiry-${enq.id}`,
        icon: "/favicon.svg",
        silent: true, // we play our own chime
      });
      n.onclick = () => {
        window.focus();
        try { n.close(); } catch (_e) { /* ignore */ }
      };
    } catch (_e) { /* no-op */ }
  };

  const refresh = useCallback(async () => {
    if (!enabled) return;
    try {
      const { data } = await api.get("/admin/lender-enquiries");
      const list = data?.items || [];
      setItems(list);

      // Compute unread against last-seen timestamp
      const since = lastSeenAt();
      const unreadCount = since
        ? list.filter((e) => e.created_at && e.created_at > since).length
        : list.length;
      setUnread(unreadCount);

      // Fire notifications for genuinely new ids (skip first poll)
      if (!primedRef.current) {
        list.forEach((e) => seenIdsRef.current.add(e.id));
        primedRef.current = true;
        return;
      }
      list.forEach((e) => {
        if (!seenIdsRef.current.has(e.id)) {
          seenIdsRef.current.add(e.id);
          // Only notify for genuinely new (created after last-seen) enquiries
          if (!since || (e.created_at && e.created_at > since)) notify(e);
        }
      });
    } catch (_e) { /* silent — polling failure shouldn't spam toasts */ }
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return;
    refresh();
    const t = setInterval(refresh, pollMs);
    return () => clearInterval(t);
  }, [enabled, pollMs, refresh]);

  const testChime = useCallback(() => playChime(), []);

  return { unread, items, muted, setMuted, markAllRead, refresh, testChime };
}

/**
 * Requests browser Notification permission if not yet decided.
 * Safe no-op on unsupported browsers.
 */
export async function ensureNotificationPermission() {
  if (typeof window === "undefined" || !("Notification" in window)) return "unsupported";
  if (Notification.permission === "granted") return "granted";
  if (Notification.permission === "denied") return "denied";
  try {
    const res = await Notification.requestPermission();
    return res;
  } catch (_e) {
    return "default";
  }
}
